"use client"

/**
 * Rich filter option nodes for column `filter.options[].node` — pairs with
 * `ColumnDef.cellKind` presets in `@exxatdesignux/ui/lib/column-filter`.
 */

import * as React from "react"

import { ProgressCell, RatingCell, SignalBarsCell } from "@/components/data-views"

import type { ColumnDef } from "@exxatdesignux/ui"

/**
 * Options whose chip is drawn by the same function that draws the cell.
 *
 * A status picker that lists plain words beside a grid of tinted chips makes
 * the reader translate between two vocabularies to answer "which one is
 * Evaluating", and the pair drifts the first time a tint is edited on one side
 * only. Passing the cell's own renderer in means there is one drawing of each
 * status and the picker cannot fall out of step with the column.
 */
export function chipFilterOptions<T extends string>(
  values: readonly T[],
  render: (value: T) => React.ReactNode,
  label?: (value: T) => string,
) {
  return values.map(value => ({
    value,
    label: label ? label(value) : value,
    node: render(value),
  }))
}

/**
 * The `renderFilterOptionValue` a hub passes to `HubTable`.
 *
 * The toolbar pill reads `filter.options[].node` on its own, but the Properties
 * drawer asks the hub instead, so without this the same filter is chips in one
 * surface and plain text in the other.
 */
export function columnFilterOptionRenderer<T>(columns: ColumnDef<T>[]) {
  return function renderFilterOptionValue(
    fieldKey: string,
    value: string,
  ): React.ReactNode {
    const column = columns.find(candidate => candidate.key === fieldKey)
    const option = column?.filter?.options?.find(
      candidate => candidate.value === value,
    )
    if (option?.node) return option.node
    return <span className="text-foreground">{option?.label ?? value}</span>
  }
}

export function ratingFilterOptions(max = 5) {
  return Array.from({ length: max }, (_, i) => {
    const value = i + 1
    return {
      value: String(value),
      label: value === 1 ? "1 star" : `${value} stars`,
      node: <RatingCell value={value} max={max} />,
    }
  })
}

export function libraryDifficultyFilterOptions() {
  return [
    { value: "easy", label: "Low", node: <SignalBarsCell level={1} tone="info" label="Low" /> },
    { value: "medium", label: "Normal", node: <SignalBarsCell level={2} tone="info" label="Normal" /> },
    { value: "hard", label: "High", node: <SignalBarsCell level={3} tone="info" label="High" /> },
  ]
}

export function signalBarsFilterOptions() {
  return [
    {
      value: "1",
      label: "Low",
      node: <SignalBarsCell level={1} tone="info" label="Low" />,
    },
    {
      value: "2",
      label: "Normal",
      node: <SignalBarsCell level={2} tone="info" label="Normal" />,
    },
    {
      value: "3",
      label: "High",
      node: <SignalBarsCell level={3} tone="info" label="High" />,
    },
  ]
}

export function progressFilterBucketOptions() {
  return [
    { value: "0", label: "0%", node: <ProgressCell value={0} /> },
    { value: "25", label: "25%", node: <ProgressCell value={25} /> },
    { value: "50", label: "50%", node: <ProgressCell value={50} /> },
    { value: "75", label: "75%", node: <ProgressCell value={75} /> },
    { value: "100", label: "100%", node: <ProgressCell value={100} /> },
  ]
}
